import { useQuery } from '@tanstack/react-query'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Cartao, Esqueleto, TituloSecao, Vazio } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { CHAVES, type ContextoPrograma } from '@/lib/sessao'
import { supabase } from '@/lib/supabase'
import type { NivelHumor } from '@/tipos/dominio'
import type { Diario, Medida, RodaVidaArea, RodaVidaResposta } from '@/tipos/tabelas'
import { Conquistas } from './Conquistas'

/**
 * Evolução (seção 12).
 *
 * Gráficos de peso, energia, humor, sono, água e atividade, mais a
 * comparação da Roda da Vida entre o início e o fim. É também o material
 * de prova de resultado que gera depoimento depois.
 */
export function PaginaEvolucao({ contexto }: { contexto: ContextoPrograma }) {
  const { matricula } = contexto

  return (
    <div className="flex flex-col gap-6 px-5 pt-6 pb-28">
      <header>
        <h1 className="font-titulo text-2xl font-bold">Sua evolução</h1>
        <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">
          Dia {contexto.dia} de {contexto.duracaoDias}
        </p>
      </header>

      <Conquistas matriculaId={matricula.id} programaId={matricula.turma.programa.id} />
      <GraficoRodaVida matriculaId={matricula.id} programaId={matricula.turma.programa.id} />
      <GraficoPeso matriculaId={matricula.id} />
      <GraficosDiario matriculaId={matricula.id} />
    </div>
  )
}

// ---------------------------------------------------------------------
// Roda da Vida — início x fim
// ---------------------------------------------------------------------

function GraficoRodaVida({
  matriculaId,
  programaId,
}: {
  matriculaId: string
  programaId: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: CHAVES.rodaVida(matriculaId),
    queryFn: async () => {
      const [{ data: areas }, { data: respostas }] = await Promise.all([
        supabase
          .from('roda_vida_areas')
          .select('*')
          .eq('programa_id', programaId)
          .eq('ativa', true)
          .order('ordem'),
        supabase.from('roda_vida_respostas').select('*').eq('matricula_id', matriculaId),
      ])

      return {
        areas: (areas ?? []) as RodaVidaArea[],
        respostas: (respostas ?? []) as RodaVidaResposta[],
      }
    },
  })

  if (isLoading) return <Esqueleto className="h-80" />
  if (!data || data.areas.length === 0) return null

  const temFinal = data.respostas.some((r) => r.momento === 'final')

  const dados = data.areas.map((area) => ({
    area: area.nome,
    inicio: data.respostas.find((r) => r.area_id === area.id && r.momento === 'inicial')?.nota ?? 0,
    fim: data.respostas.find((r) => r.area_id === area.id && r.momento === 'final')?.nota ?? 0,
  }))

  return (
    <section>
      <TituloSecao>Roda da Vida</TituloSecao>
      <Cartao>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={dados} outerRadius="72%">
            <PolarGrid stroke="var(--borda)" />
            <PolarAngleAxis
              dataKey="area"
              tick={{ fontSize: 11, fill: 'var(--tinta-suave)' }}
            />
            <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />

            <Radar
              name="Início"
              dataKey="inicio"
              stroke="#FFC93C"
              fill="#FFC93C"
              fillOpacity={0.3}
            />
            {temFinal && (
              <Radar name="Hoje" dataKey="fim" stroke="#FF6F3C" fill="#FF6F3C" fillOpacity={0.35} />
            )}
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </RadarChart>
        </ResponsiveContainer>

        {!temFinal && (
          <p className="mt-2 text-center text-xs text-[var(--tinta-apagada)]">
            No Ciclo 4 você responde de novo e vê as duas rodas sobrepostas.
          </p>
        )}
      </Cartao>
    </section>
  )
}

// ---------------------------------------------------------------------
// Peso
// ---------------------------------------------------------------------

function GraficoPeso({ matriculaId }: { matriculaId: string }) {
  const { data: medidas = [], isLoading } = useQuery({
    queryKey: CHAVES.medidas(matriculaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('medidas')
        .select('*')
        .eq('matricula_id', matriculaId)
        .order('registrado_em')

      if (error) throw error
      return data as Medida[]
    },
  })

  if (isLoading) return <Esqueleto className="h-64" />
  if (medidas.length === 0) return null

  const dados = medidas.map((m) => ({
    data: formatarData(m.registrado_em, 'd/MM'),
    peso: Number(m.peso_kg),
    imc: Number(m.imc),
  }))

  const primeira = medidas[0]
  const ultima = medidas[medidas.length - 1]
  const variacao =
    primeira && ultima ? Number(ultima.peso_kg) - Number(primeira.peso_kg) : 0

  return (
    <section>
      <TituloSecao>Peso</TituloSecao>
      <Cartao>
        {medidas.length > 1 && (
          <p className="mb-3 font-titulo text-sm font-semibold">
            <span className={variacao <= 0 ? 'text-verde' : 'text-coral'}>
              {variacao > 0 ? '+' : ''}
              {variacao.toFixed(1)} kg
            </span>
            <span className="ml-1 font-normal text-[var(--tinta-apagada)]">desde o início</span>
          </p>
        )}

        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={dados} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
            <CartesianGrid stroke="var(--borda)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="data" tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }} />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }}
              domain={['dataMin - 2', 'dataMax + 2']}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--borda)',
                background: 'var(--cartao)',
                fontSize: 13,
              }}
            />
            <Line
              type="monotone"
              dataKey="peso"
              stroke="#FF6F3C"
              strokeWidth={3}
              dot={{ r: 4, fill: '#FF6F3C' }}
              name="Peso (kg)"
            />
          </LineChart>
        </ResponsiveContainer>
      </Cartao>
    </section>
  )
}

// ---------------------------------------------------------------------
// Séries do diário
// ---------------------------------------------------------------------

const VALOR_HUMOR: Record<NivelHumor, number> = { mal: 0, neutro: 5, bem: 10 }

function GraficosDiario({ matriculaId }: { matriculaId: string }) {
  const { data: diarios = [], isLoading } = useQuery({
    queryKey: CHAVES.diarios(matriculaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('diarios')
        .select('*')
        .eq('matricula_id', matriculaId)
        .order('data')

      if (error) throw error
      return data as Diario[]
    },
  })

  if (isLoading) return <Esqueleto className="h-64" />

  if (diarios.length < 2) {
    return (
      <Vazio
        emoji="📈"
        titulo="Ainda sem dados suficientes"
        descricao="Preencha o diário por alguns dias e seus gráficos aparecem aqui."
      />
    )
  }

  const dados = diarios.map((d) => ({
    dia: `D${d.dia_numero}`,
    energia: d.energia,
    humor: d.humor ? VALOR_HUMOR[d.humor] : null,
    agua: d.copos_agua,
    atividade: d.atividade_min ?? 0,
  }))

  return (
    <>
      <section>
        <TituloSecao>Energia e humor</TituloSecao>
        <Cartao>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dados} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
              <CartesianGrid stroke="var(--borda)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid var(--borda)',
                  background: 'var(--cartao)',
                  fontSize: 13,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="energia"
                stroke="#FF6F3C"
                strokeWidth={3}
                dot={false}
                name="Energia"
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="humor"
                stroke="#FF3D7F"
                strokeWidth={3}
                dot={false}
                name="Humor"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Cartao>
      </section>

      <section>
        <TituloSecao>Água e movimento</TituloSecao>
        <Cartao>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dados} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
              <CartesianGrid stroke="var(--borda)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--tinta-apagada)' }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid var(--borda)',
                  background: 'var(--cartao)',
                  fontSize: 13,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="agua"
                stroke="#3DBD6A"
                strokeWidth={3}
                dot={false}
                name="Copos de água"
              />
              <Line
                type="monotone"
                dataKey="atividade"
                stroke="#FFC93C"
                strokeWidth={3}
                dot={false}
                name="Atividade (min)"
              />
            </LineChart>
          </ResponsiveContainer>
        </Cartao>
      </section>
    </>
  )
}
